// Deals & Sales Pipeline — types mirror the backend `dealSchema` and the
// /api/deals contracts exactly. No invented fields.

export type DealStage =
  | 'prospecting'
  | 'qualification'
  | 'proposal'
  | 'negotiation'
  | 'closed_won'
  | 'closed_lost';

export type DealPriority = 'low' | 'medium' | 'high';

/** Currencies offered in the UI. PKR intentionally excluded from selection. */
export type DealCurrency = 'INR' | 'USD' | 'EUR' | 'AED' | 'GBP';

/**
 * Populated assignee shape returned by the list endpoint
 * (`Deals.list` populates assigned_to → { _id, display_name }).
 * The detail endpoint returns the raw id string.
 */
export type AssignedRef = string | { _id?: string; id?: string; display_name?: string } | null;

export interface Deal {
  id: string;
  title: string;
  contact_name?: string;
  phone?: string;
  email?: string;
  business_name?: string;
  service_required?: string;
  additional_info?: string;
  attachments?: unknown[];
  priority?: DealPriority;
  contact_id?: string | null; // integration point: Contacts module (Phase 2)
  lead_id?: string | null; // integration point: Lead → Deal conversion (Phase 3)
  value?: number;
  /** May hold legacy values (e.g. PKR) for existing data — displayed defensively. */
  currency?: string;
  stage: DealStage;
  probability?: number;
  expected_close?: string | null;
  assigned_to?: AssignedRef;
  assigned_name?: string;
  creator_name?: string;
  created_by?: string;
  notes?: string;
  created_at?: string;
  updated_at?: string;
  /** Mapped client-side from /api/ai/deal-scores by dealId (never cross-user). */
  ai_score?: number;
}

export interface PipelineStat {
  stage: string;
  count: number;
  total_value: number;
}

export interface DealsFilters {
  stage?: string;
  assignedTo?: string;
}

export interface CreateDealPayload {
  title: string;
  contact_name?: string;
  phone?: string;
  email?: string;
  business_name?: string;
  service_required?: string;
  additional_info?: string;
  priority?: DealPriority;
  contact_id?: string | null;
  value?: number;
  currency?: DealCurrency;
  stage?: DealStage;
  probability?: number;
  expected_close?: string | null;
  assigned_to?: string | null;
  notes?: string;
}

export type UpdateDealPayload = Partial<CreateDealPayload>;

export interface UserOption {
  id: string;
  display_name: string;
  role: string;
  department?: string;
  username?: string;
}

/** Shape from GET /api/ai/deal-scores. */
export interface DealScore {
  dealId: string;
  score: number;
  stage: string;
  factors?: { label: string; value: unknown; impact: number }[];
}

/** Normalize the assignee reference to a plain id string. */
export function getAssignedId(a: AssignedRef | undefined): string {
  if (!a) return '';
  if (typeof a === 'string') return a;
  return a.id ?? a._id ?? '';
}
